/** Estado, carga e montagem do payload do formulário fiscal NFS-e (IPM + NFS-e Nacional). */

export type FiscalFormState = {
  // Topo (vale para os dois provedores)
  nfseProvider: string;
  nfseNacionalStartDate: string;
  // Comuns
  municipalServiceCode: string;
  municipalServiceName: string;
  nfseServiceDescriptionTemplate: string;
  nfseIssuerMunicipalRegistration: string;
  nfseIssRatePercent: string;
  nfseIbgeMunicipioCodigo: string;
  nfseNbsCodigo: string;
  nfseIbsCbsFinNfse: string;
  nfseIbsCbsIndFinal: string;
  nfseIbsCbsIndOp: string;
  nfseIbsCbsCst: string;
  nfseIbsCbsClassTrib: string;
  // IPM
  nfseRpsSeries: string;
  nfsePrestadorCodigoTom: string;
  nfsePrestadorTomadorEmail: string;
  ipmBaseUrl: string;
  ipmIssuePath: string;
  ipmSoapAction: string;
  ipmUsername: string;
  ipmPassword: string;
  nfseItemListaServico: string;
  nfseSoapIssRetido: string;
  nfseSoapExigibilidadeIss: string;
  nfseSoapOptanteSimplesNacional: string;
  nfseSoapIncentivoFiscal: string;
  nfseIbsCbsLocalidadeIncid: string;
  nfseRpsTipo: string;
  nfseRpsStatusDeclaracao: string;
  nfseRpsSequencia: string;
  // NFS-e Nacional
  nfseNacionalPrestadorCnpj: string;
  nfseNacionalSerie: string;
  nfseNacionalCTribNac: string;
  nfseNacionalCTribMun: string;
  nfseNacionalLocalPrestacaoIbge: string;
  nfseNacionalOpSimpNac: string;
  nfseNacionalRegApTribSn: string;
  nfseNacionalRegEspTrib: string;
  nfseNacionalTribIssqn: string;
  nfseNacionalTpRetIssqn: string;
  nfseNacionalPTotTribSn: string;
  nfseNacionalIncluirIbsCbs: boolean;
  nfseIbsCbsIndDest: string;
  nfseNacionalSignatureAlgorithm: string;
  nfseNacionalDhEmiSkewSeconds: string;
  nfseNacionalTimeoutMs: string;
  nfseNacionalSefinUrlHomologacao: string;
  nfseNacionalSefinUrlProducao: string;
  nfseNacionalAdnUrlHomologacao: string;
  nfseNacionalAdnUrlProducao: string;
  nfseNacionalDpsSequenciaHomologacao: string;
  nfseNacionalDpsSequenciaProducao: string;
};

type StringKeys = {
  [K in keyof FiscalFormState]: FiscalFormState[K] extends string ? K : never;
}[keyof FiscalFormState];

const s = (v: unknown) => (v == null ? '' : String(v));

const STRING_KEYS: StringKeys[] = [
  'nfseNacionalStartDate',
  'municipalServiceCode',
  'municipalServiceName',
  'nfseServiceDescriptionTemplate',
  'nfseIssuerMunicipalRegistration',
  'nfseIbgeMunicipioCodigo',
  'nfseNbsCodigo',
  'nfseIbsCbsFinNfse',
  'nfseIbsCbsIndFinal',
  'nfseIbsCbsIndOp',
  'nfseIbsCbsCst',
  'nfseIbsCbsClassTrib',
  'nfseRpsSeries',
  'nfsePrestadorCodigoTom',
  'nfsePrestadorTomadorEmail',
  'ipmBaseUrl',
  'ipmIssuePath',
  'ipmSoapAction',
  'ipmUsername',
  'nfseItemListaServico',
  'nfseSoapIssRetido',
  'nfseSoapExigibilidadeIss',
  'nfseSoapOptanteSimplesNacional',
  'nfseSoapIncentivoFiscal',
  'nfseIbsCbsLocalidadeIncid',
  'nfseRpsTipo',
  'nfseRpsStatusDeclaracao',
  'nfseNacionalPrestadorCnpj',
  'nfseNacionalSerie',
  'nfseNacionalCTribNac',
  'nfseNacionalCTribMun',
  'nfseNacionalLocalPrestacaoIbge',
  'nfseNacionalOpSimpNac',
  'nfseNacionalRegApTribSn',
  'nfseNacionalRegEspTrib',
  'nfseNacionalTribIssqn',
  'nfseNacionalTpRetIssqn',
  'nfseIbsCbsIndDest',
  'nfseNacionalSignatureAlgorithm',
  'nfseNacionalSefinUrlHomologacao',
  'nfseNacionalSefinUrlProducao',
  'nfseNacionalAdnUrlHomologacao',
  'nfseNacionalAdnUrlProducao'
];

const NUMBER_KEYS: StringKeys[] = [
  'nfseIssRatePercent',
  'nfseRpsSequencia',
  'nfseNacionalPTotTribSn',
  'nfseNacionalDhEmiSkewSeconds',
  'nfseNacionalTimeoutMs',
  'nfseNacionalDpsSequenciaHomologacao',
  'nfseNacionalDpsSequenciaProducao'
];

export function formFromConfig(data: Record<string, unknown>): FiscalFormState {
  const f = {
    nfseProvider: data.nfseProvider === 'nacional' ? 'nacional' : 'ipm',
    nfseNacionalIncluirIbsCbs: data.nfseNacionalIncluirIbsCbs === true,
    ipmPassword: ''
  } as FiscalFormState;
  for (const k of STRING_KEYS) (f as Record<string, unknown>)[k] = s(data[k]);
  for (const k of NUMBER_KEYS) {
    const v = data[k];
    (f as Record<string, unknown>)[k] = v != null && v !== '' ? String(v) : '';
  }
  return f;
}

export const emptyFiscalForm: FiscalFormState = formFromConfig({});

const digitsOf = (v: string) => v.replace(/\D/g, '');

/** Valida e monta o payload do PUT /billing/config. Em erro devolve o mapa campo → mensagem. */
export function buildFiscalPayload(
  form: FiscalFormState,
  opts: { ipmPasswordDirty: boolean }
): { payload: Record<string, unknown> } | { errors: Record<string, string> } {
  const fail = (errors: Record<string, string>) => ({ errors });

  const rateRaw = form.nfseIssRatePercent.replace(/\s/g, '').replace(',', '.');
  const rateNum = rateRaw === '' ? undefined : Number.parseFloat(rateRaw);
  if (rateRaw !== '' && Number.isNaN(rateNum as number)) {
    return fail({ nfseIssRatePercent: 'Alíquota ISS inválida' });
  }

  const ibge = digitsOf(form.nfseIbgeMunicipioCodigo);
  if (ibge.length > 0 && ibge.length !== 7) {
    return fail({ nfseIbgeMunicipioCodigo: 'Código IBGE deve ter 7 dígitos' });
  }

  const exactDigits: Array<[keyof FiscalFormState, number]> = [
    ['nfseNbsCodigo', 9],
    ['nfseIbsCbsLocalidadeIncid', 7],
    ['nfseIbsCbsFinNfse', 1],
    ['nfseIbsCbsIndFinal', 1],
    ['nfseIbsCbsIndOp', 6],
    ['nfseIbsCbsCst', 3],
    ['nfseIbsCbsClassTrib', 6]
  ];
  for (const [key, len] of exactDigits) {
    const raw = String(form[key]);
    if (raw.trim() !== '' && digitsOf(raw).length !== len) {
      return fail({ [key]: `Deve ter ${len} dígito(s)` });
    }
  }

  const nonNegative = (key: keyof FiscalFormState, int: boolean, msg: string) => {
    const raw = String(form[key]).trim().replace(',', '.');
    if (raw === '') return { value: undefined as number | undefined };
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || (int && !Number.isInteger(n))) {
      return { error: { [key]: msg } as Record<string, string> };
    }
    return { value: n };
  };
  const seqRps = nonNegative(
    'nfseRpsSequencia',
    true,
    'Valor inválido (use inteiro ≥ 0 para “último RPS”)'
  );
  const pTot = nonNegative('nfseNacionalPTotTribSn', false, 'Valor numérico inválido');
  const skew = nonNegative('nfseNacionalDhEmiSkewSeconds', true, 'Valor numérico inválido');
  const timeout = nonNegative('nfseNacionalTimeoutMs', true, 'Valor numérico inválido');
  const seqH = nonNegative('nfseNacionalDpsSequenciaHomologacao', true, 'Valor numérico inválido');
  const seqP = nonNegative('nfseNacionalDpsSequenciaProducao', true, 'Valor numérico inválido');
  for (const r of [seqRps, pTot, skew, timeout, seqH, seqP]) {
    if ('error' in r && r.error) return fail(r.error);
  }

  const t = (v: string) => v.trim() || undefined;
  const enumOrUndef = (v: string) => (v.trim() === '' ? undefined : v.trim());

  const payload: Record<string, unknown> = {
    // Topo
    nfseProvider: form.nfseProvider,
    nfseNacionalStartDate: form.nfseNacionalStartDate.trim(),
    // Comuns
    municipalServiceCode: t(form.municipalServiceCode),
    municipalServiceName: t(form.municipalServiceName),
    nfseServiceDescriptionTemplate: form.nfseServiceDescriptionTemplate.trim(),
    nfseIssuerMunicipalRegistration: t(form.nfseIssuerMunicipalRegistration),
    nfseIbgeMunicipioCodigo: ibge.length === 7 ? ibge : undefined,
    nfseNbsCodigo: digitsOf(form.nfseNbsCodigo) || undefined,
    nfseIbsCbsFinNfse: digitsOf(form.nfseIbsCbsFinNfse) || undefined,
    nfseIbsCbsIndFinal: digitsOf(form.nfseIbsCbsIndFinal) || undefined,
    nfseIbsCbsIndOp: digitsOf(form.nfseIbsCbsIndOp) || undefined,
    nfseIbsCbsCst: digitsOf(form.nfseIbsCbsCst) || undefined,
    nfseIbsCbsClassTrib: digitsOf(form.nfseIbsCbsClassTrib) || undefined,
    // IPM
    nfseRpsSeries: t(form.nfseRpsSeries),
    nfsePrestadorCodigoTom: t(form.nfsePrestadorCodigoTom),
    nfsePrestadorTomadorEmail: form.nfsePrestadorTomadorEmail.trim(),
    ipmBaseUrl: t(form.ipmBaseUrl),
    ipmIssuePath: t(form.ipmIssuePath),
    ipmSoapAction: t(form.ipmSoapAction),
    ipmUsername: t(form.ipmUsername),
    nfseItemListaServico: t(form.nfseItemListaServico),
    nfseSoapIssRetido: t(form.nfseSoapIssRetido),
    nfseSoapExigibilidadeIss: t(form.nfseSoapExigibilidadeIss),
    nfseSoapOptanteSimplesNacional: t(form.nfseSoapOptanteSimplesNacional),
    nfseSoapIncentivoFiscal: t(form.nfseSoapIncentivoFiscal),
    nfseIbsCbsLocalidadeIncid: digitsOf(form.nfseIbsCbsLocalidadeIncid) || undefined,
    nfseRpsTipo: t(form.nfseRpsTipo),
    nfseRpsStatusDeclaracao: t(form.nfseRpsStatusDeclaracao),
    // NFS-e Nacional
    nfseNacionalPrestadorCnpj: digitsOf(form.nfseNacionalPrestadorCnpj),
    nfseNacionalSerie: digitsOf(form.nfseNacionalSerie),
    nfseNacionalCTribNac: digitsOf(form.nfseNacionalCTribNac),
    nfseNacionalCTribMun: digitsOf(form.nfseNacionalCTribMun),
    nfseNacionalLocalPrestacaoIbge: digitsOf(form.nfseNacionalLocalPrestacaoIbge),
    nfseNacionalOpSimpNac: enumOrUndef(form.nfseNacionalOpSimpNac),
    nfseNacionalRegApTribSn: enumOrUndef(form.nfseNacionalRegApTribSn),
    nfseNacionalRegEspTrib: enumOrUndef(form.nfseNacionalRegEspTrib),
    nfseNacionalTribIssqn: enumOrUndef(form.nfseNacionalTribIssqn),
    nfseNacionalTpRetIssqn: enumOrUndef(form.nfseNacionalTpRetIssqn),
    nfseNacionalIncluirIbsCbs: form.nfseNacionalIncluirIbsCbs,
    nfseIbsCbsIndDest: enumOrUndef(form.nfseIbsCbsIndDest),
    nfseNacionalSignatureAlgorithm: enumOrUndef(form.nfseNacionalSignatureAlgorithm),
    nfseNacionalSefinUrlHomologacao: form.nfseNacionalSefinUrlHomologacao.trim(),
    nfseNacionalSefinUrlProducao: form.nfseNacionalSefinUrlProducao.trim(),
    nfseNacionalAdnUrlHomologacao: form.nfseNacionalAdnUrlHomologacao.trim(),
    nfseNacionalAdnUrlProducao: form.nfseNacionalAdnUrlProducao.trim()
  };
  if (rateNum !== undefined) payload.nfseIssRatePercent = rateNum;
  const num = (r: { value?: number | undefined } | { error: Record<string, string> }) =>
    'value' in r ? r.value : undefined;
  if (num(seqRps) !== undefined) payload.nfseRpsSequencia = num(seqRps);
  if (num(pTot) !== undefined) payload.nfseNacionalPTotTribSn = num(pTot);
  if (num(skew) !== undefined) payload.nfseNacionalDhEmiSkewSeconds = num(skew);
  if (num(timeout) !== undefined) payload.nfseNacionalTimeoutMs = num(timeout);
  if (num(seqH) !== undefined) payload.nfseNacionalDpsSequenciaHomologacao = num(seqH);
  if (num(seqP) !== undefined) payload.nfseNacionalDpsSequenciaProducao = num(seqP);
  if (opts.ipmPasswordDirty && form.ipmPassword.trim()) {
    payload.ipmPassword = form.ipmPassword.trim();
  }
  return { payload };
}
